import { CellContext, Row } from '@tanstack/react-table'
import {
  commonActionColumn,
  renderGradeColumn,
  renderHeaderTooltip
} from '@utils/assessmentReportUtils'
import { Flex } from 'antd'
import { AssessmentItem } from 'src/types/type'

import {
  Database_Procs_GymPercentAggregatedByLevel_Item,
  Entities_Assessment_GymsFactFluencyTeam_GymFluencyDetail
} from '@suntex.fim/api-client'
import { GymTableCell, getGymColumnKeys } from '@suntex.fim/assessment'
import { $ArithmeticOperation, $GymMode, GymMode } from '@suntex.fim/enums'
import { ColumnType, Tooltip } from '@suntex.fim/ui'

import {
  generateScrollableAssessmentCommonColumns,
  generateScrollableAssessmentCommonColumnsForSchool
} from './CommonColumns'

export type GymReportData = Partial<
  | (Database_Procs_GymPercentAggregatedByLevel_Item &
      Partial<Entities_Assessment_GymsFactFluencyTeam_GymFluencyDetail>)
  | Entities_Assessment_GymsFactFluencyTeam_GymFluencyDetail
> &
  AssessmentItem

/**
 * Common labels for selectable columns.
 */
export const hidableSelectLabel: Record<string, string> = {
  userId: 'User ID',
  displayName: 'Display Name',
  stickers: 'Stickers',
  Addition: 'Addition',
  Subtraction: 'Subtraction',
  Multiplication: 'Multiplication',
  Division: 'Division'
}

// Re-export GymTableCell and getGymColumnKeys for backward compatibility
export { GymTableCell, getGymColumnKeys }

/**
 * Generates table columns for gym report.
 */
export function columns({
  graphColor,
  level,
  sendMessage
}: {
  graphColor: string
  level: GymMode
  sendMessage: (t: Row<GymReportData>[]) => void
}): ColumnType<GymReportData>[] {
  const columnKeys = getGymColumnKeys(level)

  const conditionalColumns = columnKeys.map((opSlug) => ({
    header: $ArithmeticOperation.describe($ArithmeticOperation.fromSlug(opSlug))
      .label,
    accessorFn: ({ resultData }: { resultData: Record<string, any> }) =>
      resultData?.[opSlug] ?? {},
    id: opSlug,
    size: 180,
    enableSorting: false,
    cell: (info: any) => (
      <GymTableCell
        steps={level !== GymMode.K2 ? 12 : 10}
        opSlug={opSlug}
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        rowId={info.row.id}
        infoValue={info.getValue() as { [key: string]: boolean }} // Use type assertion
        level={level}
        graphColor={graphColor}
      />
    )
  }))

  const actionColumn = commonActionColumn<GymReportData>(
    hidableSelectLabel,
    sendMessage
  )

  return [
    actionColumn,
    ...generateScrollableAssessmentCommonColumns<GymReportData>(),
    ...conditionalColumns
  ]
}

/**
 * Labels for school columns.
 */
export const schoolHidableSelectLabel: Record<string, string> = {
  name: 'Name',
  desc: 'Class Name',
  contactName: 'Teacher',
  grade: 'Grade',
  students: 'Students',
  stickers: 'Stickers',
  Addition: 'Addition',
  Subtraction: 'Subtraction',
  Multiplication: 'Multiplication',
  Division: 'Division'
}

/**
 * Generates table columns for school gym report.
 */
export function schoolColumns({
  level
}: {
  level: GymMode
}): ColumnType<GymReportData>[] {
  const gymModeSlug = $GymMode.describe(level).slug
  const columnKeys = getGymColumnKeys(level)
  const conditionalColumns = columnKeys.map(
    (opSlug) =>
      ({
        header: $ArithmeticOperation.describe(
          $ArithmeticOperation.fromSlug(opSlug)
        ).label,
        accessorFn: (info) => (info ? info[`gym_${gymModeSlug}_${opSlug}`] : 0),
        id: opSlug,
        size: 180,
        enableSorting: false,
        cell: (info: CellContext<GymReportData, number>) => {
          const responseKey =
            `gym_${gymModeSlug}_${opSlug}` as keyof Database_Procs_GymPercentAggregatedByLevel_Item

          const completionPercentage = info.row.original
            ? (info.row.original as unknown as Record<string, number>)[
                responseKey
              ]
            : 0

          return (
            <div className="progress-bar-container">
              <Tooltip
                classNames={{ root: 'progress-tooltip' }}
                placement="top"
                arrow={false}
                title={
                  <div>
                    <b>
                      {completionPercentage
                        ? `${completionPercentage}% Complete`
                        : 'Data not available'}
                    </b>
                  </div>
                }
              >
                <Flex className="progress-bar">
                  <div
                    className="progress-bar-fill"
                    style={{ width: `${completionPercentage}%` }}
                  />
                </Flex>
              </Tooltip>
            </div>
          )
        }
      }) as ColumnType<GymReportData>
  )

  const schoolCommonColumns =
    generateScrollableAssessmentCommonColumnsForSchool<GymReportData>()
  return [
    ...schoolCommonColumns.slice(0, 3),
    {
      header: () => renderHeaderTooltip('Grade'),
      size: 110,
      accessorKey: 'grade',
      cell: (info) => {
        return renderGradeColumn(info.getValue())
      },
      id: 'grade',
      enableSorting: true
    },
    ...schoolCommonColumns.slice(4),
    ...conditionalColumns
  ]
}
